import React, { forwardRef, useContext } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts'; // Import BarChart
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { getColor, rgbaColor } from 'helpers/utils';
import AppContext from 'context/Context';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart // Use BarChart
]);

const tooltipFormatter = params => {
  return params
    .map(
      ({ value, seriesName, borderColor }) =>
        `<div class="dot me-1 fs--2 d-inline-block" style="background-color: ${borderColor}"></div>
              <span class='text-600'>${seriesName}: ${value}</span>`
    )
    .join('<br/>');
};

const getOptions = (data, isDark) => ({
  color: getColor('gray-100'),
  tooltip: {
    trigger: 'axis',
    padding: [7, 10],
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-100'),
    textStyle: { color: isDark ? '#fff' : getColor('dark') },
    borderWidth: 1,
    formatter: tooltipFormatter,
    transitionDuration: 0
  },
  legend: {
    data: data.seriesName, // Update legend data
    show: true,
    textStyle: { color: isDark ? '#fff' : getColor('gray-700') }
  },
  xAxis: {
    type: 'category',
    position: 'bottom',
    data: data.xAxisData,
    boundaryGap: true, // Change boundaryGap to true for bar chart
    axisPointer: {
      lineStyle: {
        color: getColor('gray-200'),
        type: 'line'
      }
    },
    splitLine: { show: false },
    axisLine: {
      lineStyle: {
        color: getColor('gray-200'),
        type: 'line'
      }
    },
    axisTick: { show: false },
    axisLabel: {
      color: isDark ? '#fff' : getColor('gray-500'),
      formatter(value) {
        return value;
      },
      interval: 0,
      margin: 15,
      showMinLabel: true,
      showMaxLabel: true,
      align: 'center'
    }
  },
  yAxis: {
    type: 'value',
    splitNumber: 3,
    axisPointer: { show: false },
    splitLine: {
      lineStyle: {
        color: getColor('gray-200'),
        type: 'line'
      }
    },
    boundaryGap: [0, '10%'], // Change boundaryGap for bar chart
    axisLabel: {
      showMinLabel: false,
      show: true,
      color: isDark ? '#fff' : getColor('gray-400'),
      formatter: value => value
    },
    axisTick: { show: false },
    axisLine: { show: false }
  },
  series: [
    {
      name: data.seriesName[0],
      type: 'bar', // Change chart type to 'bar'
      data: data.totalSupportCalls,
      barWidth: '20%', // Set bar width
      itemStyle: {
        color: getColor('primary'),
      },
      emphasis: {
        scale: true
      },
      label: { // Add labels to the bars
        show: true,
        position: 'top', // Set label position
        color: isDark ? '#fff' : getColor('dark')
      }
    },
    {
      name: data.seriesName[1],
      type: 'bar', // Change chart type to 'bar'
      data: data.resolved,
      barWidth: '10%', // Set bar width
      itemStyle: {
        color: getColor('success'),
      },
      emphasis: {
        scale: true
      },
      label: { // Add labels to the bars
        show: true,
        position: 'top', // Set label position
        color: isDark ? '#fff' : getColor('dark')
      }
    },
    {
      name: data.seriesName[2],
      type: 'bar', // Change chart type to 'bar'
      data: data.temporaryResolved,
      barWidth: '10%', // Set bar width
      itemStyle: {
        color: getColor('warning'),
      },
      emphasis: {
        scale: true
      },
      label: { // Add labels to the bars
        show: true,
        position: 'top', // Set label position
        color: isDark ? '#fff' : getColor('dark')
      }
    },
    {
      name: data.seriesName[3],
      type: 'bar', // Change chart type to 'bar'
      data: data.permanentlyResolved,
      barWidth: '10%', // Set bar width
      itemStyle: {
        color: getColor('info'),
      },
      emphasis: {
        scale: true
      },
      label: { // Add labels to the bars
        show: true,
        position: 'top', // Set label position
        color: isDark ? '#fff' : getColor('dark')
      }
    },
    {
      name: data.seriesName[4],
      type: 'bar', // Change chart type to 'bar'
      data: data.pending,
      barWidth: '10%', // Set bar width
      itemStyle: {
        color: getColor('danger'),
      },
      emphasis: {
        scale: true
      },
      label: { // Add labels to the bars
        show: true,
        position: 'top', // Set label position
        color: isDark ? '#fff' : getColor('dark')
      }
    }
  ],
  grid: {
    right: '10px',
    left: '30px',
    bottom: '15%',
    top: '5%'
  }
});

const BarChartComponent = forwardRef(({ data }, ref) => {
  const { config } = useContext(AppContext);
  const { isDark } = config;
  return (
    <ReactEChartsCore ref={ref} echarts={echarts} option={getOptions(data, isDark)} />
  );
});

BarChartComponent.propTypes = {
  data: PropTypes.shape({
    xAxisData: PropTypes.array,
    seriesName: PropTypes.string,
    totalSupportCalls: PropTypes.array
  })
};

export default BarChartComponent;
